import type { Dispatch } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import { Spinner } from '@/components/atoms/Spinner';
import { ModalFooterStart } from '@/components/molecules/ModalComponent';

import type { CageWarning, WizardValidationError } from '../operateWizardHelpers';
import type { OperateWizardAction, OperateWizardState } from '../operateWizardReducer';

import { getEffectiveSteps, stepCanProceed } from './wizardSteps';

interface WizardFooterProps {
    state: OperateWizardState;
    dispatch: Dispatch<OperateWizardAction>;
    onClose: () => void;
    validationErrors: WizardValidationError[];
    cageWarnings: CageWarning[];
    isSubmitting: boolean;
    onSubmit: () => void;
    includeBioscopeStep: boolean;
}

export function WizardFooter(props: WizardFooterProps) {
    const {
        state,
        dispatch,
        onClose,
        validationErrors,
        cageWarnings,
        isSubmitting,
        onSubmit,
        includeBioscopeStep,
    } = props;
    const { t } = useTranslation('fishGroups');

    const steps = getEffectiveSteps(state, includeBioscopeStep);
    const currentIndex = steps.indexOf(state.step);
    const isFirstStep = currentIndex === 0;
    const isLastStep = currentIndex === steps.length - 1;

    const canGoNext = stepCanProceed(state, validationErrors);
    // Execute is the final action: it requires a clean validation and no
    // cage warnings (otherwise the backend would 400 for occupancy). We
    // also block while a previous submit is in flight to prevent dupes.
    // The operation date is required — an irreversible composition change
    // with no recorded date leaves the lineage untraceable.
    const canExecute =
        validationErrors.length === 0 &&
        cageWarnings.length === 0 &&
        state.destinations.length > 0 &&
        state.operationDate !== null &&
        !isSubmitting;

    const goBack = () => {
        const prevStep = steps[currentIndex - 1];
        if (prevStep) dispatch({ type: 'setStep', step: prevStep });
    };
    const goNext = () => {
        const nextStep = steps[currentIndex + 1];
        if (nextStep) dispatch({ type: 'setStep', step: nextStep });
    };

    return (
        <>
            <ModalFooterStart>
                <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
                    {t('actions.operate.nav.cancel')}
                </Button>
            </ModalFooterStart>
            {!isFirstStep && (
                <Button variant="secondary" onClick={goBack} disabled={isSubmitting}>
                    {t('actions.operate.nav.back')}
                </Button>
            )}
            {!isLastStep && state.step !== 'kind' && (
                <Button variant="primary" onClick={goNext} disabled={!canGoNext}>
                    {t('actions.operate.nav.next')}
                </Button>
            )}
            {/*
              Execute is the terminal action — it only belongs on the explicit
              confirm step. Guarding against `isLastStep` alone is not enough:
              when the operator is still on the chooser, `getStepsForKind(null)`
              returns just `['kind']`, so `'kind'` is both first and "last", and
              Execute would show up disabled.
            */}
            {state.step === 'confirm' && (
                <Button
                    variant="primary"
                    onClick={onSubmit}
                    disabled={!canExecute}
                    icon={isSubmitting ? <Spinner /> : undefined}>
                    {t('actions.operate.nav.execute')}
                </Button>
            )}
        </>
    );
}
